import { useEffect, useState } from "react";
import { BookOpen, Globe, Link2, Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { getTrainingDocs } from "../../lib/aiAgent";
import { AI_TRAINING_DOCS_VISIBLE } from "../../lib/featureFlags";

/**
 * Fonti fissate per la conversazione: documenti di piattaforma e indirizzi.
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md §9sexies.
 *
 * Risponde a una richiesta precisa del coach — *«come posso dare delle fonti
 * nel prompt?»* — e a una esigenza più profonda: un programma motivato da
 * «pratica comune» vale meno di uno motivato da un documento che il coach
 * riconosce. Quello che si fissa qui viene nominato nel contesto iniziale, e
 * al modello si chiede di basarsi su quello e di citarlo.
 *
 * La **ricerca web** è separata perché non è disponibile ovunque: Anthropic la
 * espone in modo compatibile con gli strumenti, OpenAI no. Mostrarla dove non
 * c'è sarebbe una promessa non mantenuta.
 */
export function SourcePicker({
	documentIds,
	onDocumentsChange,
	urls,
	onUrlsChange,
	webSearch,
	onWebSearchChange,
	webSearchAvailable,
}) {
	const [open, setOpen] = useState(false);
	const [docs, setDocs] = useState([]);
	const [draftUrl, setDraftUrl] = useState("");

	useEffect(() => {
		if (!AI_TRAINING_DOCS_VISIBLE || !open || docs.length) return;
		getTrainingDocs()
			.then((list) => setDocs(Array.isArray(list) ? list : []))
			.catch(() => setDocs([]));
	}, [open, docs.length]);

	const pinned = documentIds.length + urls.length + (webSearch ? 1 : 0);

	const addUrl = () => {
		const value = draftUrl.trim();
		if (!value) return;
		const normalised = value.startsWith("http") ? value : `https://${value}`;
		if (!urls.includes(normalised)) onUrlsChange([...urls, normalised]);
		setDraftUrl("");
	};

	return (
		<div className="mb-2">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="inline-flex items-center gap-1.5 rounded-sm border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
			>
				<BookOpen className="h-3 w-3" />
				Fonti
				{pinned > 0 && <span className="text-volt">· {pinned}</span>}
			</button>

			{open && (
				<div className="mt-2 space-y-3 rounded-sm border border-border bg-secondary/30 p-2">
					{webSearchAvailable ? (
						<label className="flex items-start gap-2 text-[11px]">
							<input
								type="checkbox"
								checked={webSearch}
								onChange={(event) => onWebSearchChange(event.target.checked)}
								className="mt-0.5"
							/>
							<span>
								<span className="font-medium">Cerca online</span>
								<span className="block text-muted-foreground">
									L'assistente cerca fonti e le cita. Costa circa 1 centesimo a
									ricerca, sul tuo account.
								</span>
							</span>
						</label>
					) : (
						<p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
							<Globe className="mt-0.5 h-3 w-3 shrink-0" />
							La ricerca online è disponibile con un account Anthropic. Con
							OpenAI puoi comunque indicare qui sotto gli indirizzi da leggere.
						</p>
					)}

					{AI_TRAINING_DOCS_VISIBLE && docs.length > 0 && (
						<div className="space-y-1">
							<p className="text-[10px] uppercase tracking-wide text-muted-foreground">
								Documenti di piattaforma
							</p>
							{docs.slice(0, 20).map((doc) => (
								<label
									key={doc.document_id}
									className="flex items-start gap-2 text-[11px]"
								>
									<input
										type="checkbox"
										checked={documentIds.includes(doc.document_id)}
										onChange={(event) =>
											onDocumentsChange(
												event.target.checked
													? [...documentIds, doc.document_id]
													: documentIds.filter((id) => id !== doc.document_id),
											)
										}
										className="mt-0.5"
									/>
									<span className="min-w-0 truncate">{doc.title}</span>
								</label>
							))}
						</div>
					)}

					<div className="space-y-1">
						<p className="text-[10px] uppercase tracking-wide text-muted-foreground">
							Indirizzi da consultare
						</p>
						{urls.map((url) => (
							<div key={url} className="flex items-center gap-1 text-[11px]">
								<Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
								<span className="min-w-0 flex-1 truncate">{url}</span>
								<button
									type="button"
									onClick={() => onUrlsChange(urls.filter((u) => u !== url))}
									aria-label="Rimuovi"
									className="text-muted-foreground hover:text-blaze"
								>
									<X className="h-3 w-3" />
								</button>
							</div>
						))}
						<div className="flex gap-1">
							<Input
								value={draftUrl}
								onChange={(event) => setDraftUrl(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault();
										addUrl();
									}
								}}
								placeholder="https://…"
								className="h-7 rounded-sm text-[11px]"
							/>
							<Button
								type="button"
								size="icon"
								variant="outline"
								onClick={addUrl}
								className="h-7 w-7 shrink-0 rounded-sm"
								aria-label="Aggiungi indirizzo"
							>
								<Plus className="h-3 w-3" />
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
